import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../Footer/Footer';
import config from '../../../config/environment';
import { focusInvalidField, getFieldErrors } from '../../../utils/formFieldErrors';

const Page = styled.div`
  min-height: 100vh;
  background: #FAF7F0;
  padding-top: 158px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

const Main = styled.main`
  flex: 1;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 3rem 1.5rem 5rem;
`;

const Title = styled.h1`
  margin: 0 0 1.75rem;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(2.4rem, 5vw, 3.4rem);
  font-weight: 500;
  line-height: 1.05;
  color: #101d40;
`;

const Card = styled.form`
  background: #fff;
  border: 1px solid rgba(16, 29, 64, 0.08);
  padding: 2rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #101d40;
`;

const LabelRow = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.55rem;
`;

const Hint = styled.span`
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  text-transform: none;
  color: rgba(16, 29, 64, 0.62);
`;

const control = `
  width: 100%;
  border: 1px solid rgba(16, 29, 64, 0.18);
  background: #FAF7F0;
  color: #101d40;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1rem;
  letter-spacing: 0;
  text-transform: none;
  padding: 0.85rem 0.9rem;
  outline: none;

  &:focus {
    border-color: #9d6d49;
  }

  &[aria-invalid='true'] {
    border-color: #8a2b2b;
    background: #fdf3f3;
  }
`;

const FieldError = styled.span`
  font-size: 0.8rem;
  letter-spacing: 0;
  text-transform: none;
  color: #8a2b2b;
`;

const Input = styled.input`
  ${control}
`;

const TextArea = styled.textarea`
  ${control}
  min-height: 7rem;
  resize: vertical;
`;

const Submit = styled.button`
  align-self: flex-start;
  margin-top: 0.25rem;
  border: 0;
  background: #101d40;
  color: #fff;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.95rem 1.4rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const TripLink = styled(Link)`
  display: inline-block;
  margin-top: 1.5rem;
  border: 1px solid #101d40;
  background: transparent;
  color: #101d40;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 0.95rem 1.4rem;

  &:hover {
    background: #101d40;
    color: #fff;
  }
`;

const Notice = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.95rem;
  letter-spacing: 0;
  text-transform: none;
  line-height: 1.5;
  color: ${props => (props.$error ? '#8a2b2b' : '#101d40')};
`;

const ThankYou = styled.div`
  background: #fff;
  border: 1px solid rgba(16, 29, 64, 0.08);
  padding: 2.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: system-ui, -apple-system, sans-serif;
  color: #101d40;
`;

const ThankYouTitle = styled.h2`
  margin: 0;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.8rem, 4vw, 2.4rem);
  font-weight: 500;
  line-height: 1.1;
  color: #101d40;
`;

const ThankYouText = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
`;

const AnotherButton = styled(Submit)`
  border: 1px solid #101d40;
  background: transparent;
  color: #101d40;

  &:hover {
    background: #101d40;
    color: #fff;
  }
`;

const emptyForm = {
  fullName: '',
  school: '',
  address: '',
  email: '',
  packs: '',
};

const SchoolBauble = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, nofollow');
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const updateField = event => {
    const { name, value } = event.target;
    if (fieldErrors[name]) {
      setFieldErrors(({ [name]: _cleared, ...rest }) => rest);
      if (Object.keys(fieldErrors).length === 1) setNotice(null);
    }
    if (name === 'packs' && value !== '') {
      const packs = Math.min(10, Math.max(1, Math.floor(Number(value))));
      setForm(current => ({ ...current, packs: Number.isNaN(packs) ? '' : String(packs) }));
      return;
    }
    setForm(current => ({ ...current, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const { errors, firstInvalid } = getFieldErrors(event.currentTarget);
    setFieldErrors(errors);
    if (firstInvalid) {
      setNotice({ error: true, message: 'Please complete the highlighted fields.' });
      focusInvalidField(firstInvalid);
      return;
    }
    setNotice(null);
    setSubmitting(true);

    try {
      const response = await fetch(`${config.apiEndpoint}/belvoir/v1/school-bauble`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          fullName: form.fullName.trim(),
          school: form.school.trim(),
          address: form.address.trim(),
          email: form.email.trim(),
          packs: Number(form.packs),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong. Please try again.');
      }

      setSubmitted({ school: form.school.trim(), packs: Number(form.packs) });
      setForm(emptyForm);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setNotice({
        error: true,
        message: error.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Helmet>
        <title>School Christmas Bauble Design sign up form | Belvoir Castle</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Main>
        <Title>School Christmas Bauble Design sign up form</Title>
        {submitted ? (
          <ThankYou role="status">
            <ThankYouTitle>Thank you for signing up</ThankYouTitle>
            <ThankYouText>
              We have received your request for {submitted.packs} {submitted.packs === 1 ? 'pack' : 'packs'}
              {submitted.school ? ` for ${submitted.school}` : ''}. Our team will be in touch with the
              next steps for your bauble design packs.
            </ThankYouText>
            <ThankYouText>
              We look forward to seeing your pupils' festive designs.
            </ThankYouText>
            <AnotherButton type="button" onClick={() => setSubmitted(null)}>
              Submit another request
            </AnotherButton>
          </ThankYou>
        ) : (
          <Card onSubmit={handleSubmit} noValidate>
            {notice && <Notice $error={notice.error}>{notice.message}</Notice>}
            <Field>
              Full name
              <Input
                name="fullName"
                type="text"
                autoComplete="name"
                value={form.fullName}
                onChange={updateField}
                required
                aria-invalid={fieldErrors.fullName ? 'true' : undefined}
              />
              {fieldErrors.fullName && <FieldError>{fieldErrors.fullName}</FieldError>}
            </Field>
            <Field>
              School
              <Input
                name="school"
                type="text"
                autoComplete="organization"
                value={form.school}
                onChange={updateField}
                required
                aria-invalid={fieldErrors.school ? 'true' : undefined}
              />
              {fieldErrors.school && <FieldError>{fieldErrors.school}</FieldError>}
            </Field>
            <Field>
              Address
              <TextArea
                name="address"
                autoComplete="street-address"
                value={form.address}
                onChange={updateField}
                required
                aria-invalid={fieldErrors.address ? 'true' : undefined}
              />
              {fieldErrors.address && <FieldError>{fieldErrors.address}</FieldError>}
            </Field>
            <Field>
              Email Address
              <Input
                name="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={updateField}
                required
                aria-invalid={fieldErrors.email ? 'true' : undefined}
              />
              {fieldErrors.email && <FieldError>{fieldErrors.email}</FieldError>}
            </Field>
            <Field>
              <LabelRow>
                Number of Packs Required
                <Hint>(Each pack contains 30 worksheets and 1 winners bauble) (Max 10)</Hint>
              </LabelRow>
              <Input
                name="packs"
                type="number"
                min="1"
                max="10"
                step="1"
                inputMode="numeric"
                value={form.packs}
                onChange={updateField}
                required
                aria-invalid={fieldErrors.packs ? 'true' : undefined}
              />
              {fieldErrors.packs && <FieldError>{fieldErrors.packs}</FieldError>}
            </Field>
            <Submit type="submit" disabled={submitting}>
              {submitting ? 'Sending' : 'Submit'}
            </Submit>
          </Card>
        )}
        <TripLink to="/your-visit/schools">Book a school trip with us</TripLink>
      </Main>
      <Footer />
    </Page>
  );
};

export default SchoolBauble;
